import {
  type CSSProperties,
  type FunctionComponent,
  type MouseEvent,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import {
  ADS_BASE_URL,
  safeAdImageUrl,
  safeAdUrl,
} from "@/shared/ui/ad/safeAdUrl";

export type AdCategory = "safe" | "r18";

/** /ads/serve が返す広告1件 */
interface Ad {
  id: string;
  kind: "regular" | "house" | "placeholder";
  category: AdCategory;
  title: string;
  body: string;
  image_url: string;
  image_width: number | null;
  image_height: number | null;
  image_alt: string | null;
  /** 通常広告は URL 文字列。null はリンク無し広告（ブランド露出のみ）。 */
  click_url: string | null;
}

interface BmgBannerProps {
  /** 広告枠識別子。省略時は "default"。 */
  slot?: string;
  /** 広告の配信カテゴリ。省略時は安全広告。 */
  category?: AdCategory;
  /** 広告なし確定後も枠を維持する。 */
  reserveEmptySpace?: boolean;
  /** 取得前から確保する枠の寸法。省略時は468×60。 */
  slotDimensions?: { width: number; height: number };
  /** 初期表示のLCP候補として画像取得を優先する。 */
  priority?: boolean;
  /** R18広告をぼかし、操作で開示する。 */
  concealR18?: boolean;
}

/**
 * slot ごとの広告取得結果をページロード単位でキャッシュする。
 *
 * 同接数表示（`OnlineUsersIndicatorElement`）と同じ調子で、モジュールレベルの
 * 共有 Promise に取得結果を保持する。SPA なのでカタログ⇄スレッドの画面遷移では
 * モジュールが再評価されず、同一 category/slot への `/ads/serve` は基本1回だけ走る。
 * 大量のページ表示で広告サーバ（Workers）へ毎回直撃するのを防ぐ。
 *
 * ただし SPA（特にスマホ）はフル再読み込みがほぼ発生しないため、それだけだと
 * セッション中ずっと同じ広告が固定表示されてしまう。そこで {@link AD_CACHE_TTL_MS}
 * の有効期限を設け、期限切れ後に**バナーが次にマウントされた時だけ**取り直す。
 * 画面遷移（= マウント）が「再読み込みの代わり」になる。タイマーや
 * visibilitychange 監視を使わないので、アイドル時の負荷はゼロ。
 */
const AD_CACHE_TTL_MS: number = 10 * 1000;
const AD_DIMENSION_MAX_PX = 4096;

interface AdCacheEntry {
  promise: Promise<Ad | null>;
  /** 取得を開始した時刻（epoch ms）。TTL 判定に使う。 */
  fetchedAt: number;
}

interface AdState {
  category: AdCategory;
  slot: string;
  ad: Ad | null;
}

const adCache: Map<string, AdCacheEntry> = new Map();

function parseAd(value: unknown, category: AdCategory): Ad | null {
  if (typeof value !== "object" || value === null) return null;
  const record = value as Record<string, unknown>;
  const kind = record.kind;
  const imageWidth = record.image_width;
  const imageHeight = record.image_height;
  const imageAlt = record.image_alt;
  if (
    typeof record.id !== "string" ||
    (kind !== "regular" && kind !== "house" && kind !== "placeholder") ||
    record.category !== category ||
    typeof record.title !== "string" ||
    typeof record.body !== "string" ||
    typeof record.image_url !== "string" ||
    (imageWidth !== null &&
      (typeof imageWidth !== "number" ||
        !Number.isInteger(imageWidth) ||
        imageWidth <= 0 ||
        imageWidth > AD_DIMENSION_MAX_PX)) ||
    (imageHeight !== null &&
      (typeof imageHeight !== "number" ||
        !Number.isInteger(imageHeight) ||
        imageHeight <= 0 ||
        imageHeight > AD_DIMENSION_MAX_PX)) ||
    (imageAlt !== null && typeof imageAlt !== "string") ||
    (record.click_url !== null && typeof record.click_url !== "string")
  ) {
    return null;
  }
  return {
    id: record.id,
    kind,
    category,
    title: record.title,
    body: record.body,
    image_url: record.image_url,
    image_width: imageWidth,
    image_height: imageHeight,
    image_alt: imageAlt,
    click_url: record.click_url,
  };
}

function parseAdResponse(value: unknown, category: AdCategory): Ad | null {
  if (typeof value !== "object" || value === null) return null;
  const record = value as Record<string, unknown>;
  if (record.category !== category || !Array.isArray(record.ads)) return null;

  const ads = record.ads.map((ad) => parseAd(ad, category));
  if (ads.some((ad) => ad === null)) return null;
  return ads[0] ?? null;
}

function fetchAd(slot: string, category: AdCategory): Promise<Ad | null> {
  const now = performance.now();
  const cacheKey = `${category}\u0000${slot}`;
  const cached = adCache.get(cacheKey);
  // TTL 内のキャッシュはそのまま再利用。期限切れ/未取得なら取り直す。
  if (cached && now - cached.fetchedAt < AD_CACHE_TTL_MS) {
    return cached.promise;
  }

  const promise = (async (): Promise<Ad | null> => {
    try {
      const params = new URLSearchParams({ slot, category, n: "1" });
      const res = await fetch(`${ADS_BASE_URL}/ads/serve?${params.toString()}`);
      // 204 は 2xx なので res.ok では弾けない。明示的に除外する。
      if (res.status === 204 || !res.ok) return null;
      return parseAdResponse(await res.json(), category);
    } catch {
      // ネットワークエラー / 障害時は広告なし扱い
      return null;
    }
  })();

  adCache.set(cacheKey, { promise, fetchedAt: now });
  return promise;
}

/**
 * ぶるもげちゃん広告サーバのバナーを表示する。
 *
 * - 取得は {@link fetchAd} 経由でページロード単位にキャッシュされる
 *   （同接数表示と同じ調子）。マウントごとには fetch しない。
 * - `reserveEmptySpace`指定時は、取得確定で広告なし（204/エラー/不正）でも
 *   透明な広告枠を維持し、後続コンテンツを上へ移動させない。
 * - reserveEmptySpace時はslotDimensions（省略時468×60）を取得前後で維持し、
 *   画像は枠内に収める。それ以外は取得したimage_width/image_heightを使う。
 *   極端な縦横比でも広告枠の高さは50svh以下に収める。
 *
 * クリックは `click_url`（= `/ads/click/:id`）アクセス時にサーバ側で
 * 302 リダイレクト + click 計上される。
 */
export const BmgBanner: FunctionComponent<BmgBannerProps> = ({
  slot = "default",
  category = "safe",
  reserveEmptySpace = false,
  slotDimensions,
  priority = false,
  concealR18 = false,
}: BmgBannerProps) => {
  // undefined = 取得中, AdState.ad が null = 広告なし確定, AdState.ad = 取得済み。
  // 取得中も枠を確保したいので初期値は undefined（null と区別する）。
  const [adState, setAdState] = useState<AdState | undefined>(undefined);

  useEffect(() => {
    let active = true;
    setAdState(undefined);
    void fetchAd(slot, category).then((result) => {
      // 共有 Promise を使うため AbortController は使わず、
      // アンマウント後の setState だけ active フラグで防ぐ。
      if (active) setAdState({ category, slot, ad: result });
    });
    return (): void => {
      active = false;
    };
  }, [slot, category]);

  // カテゴリまたはslot切り替え直後は、effect が実行される前の1 renderも旧広告を使わない。
  const displayedAd =
    adState?.category === category && adState.slot === slot
      ? adState.ad
      : undefined;

  // 取得済みなら URL を検証する。相対パスは配信ベースに解決し、許可ドメイン以外や
  // 危険なスキームは弾く。取得済みで画像URLが不正なら空の予約枠として扱う。
  const imageUrl = displayedAd ? safeAdImageUrl(displayedAd.image_url) : null;
  if (displayedAd !== undefined && !imageUrl && !reserveEmptySpace) return null;

  // click_url が null（リンク無し広告）/ 不正URLのときはリンクを作らず、
  // <img> 単体で描画する（アンカー無し・onClick 無しでクリックしても何も起きない）。
  const clickUrl =
    imageUrl && displayedAd ? safeAdUrl(displayedAd.click_url) : null;

  const validSlotDimensions =
    slotDimensions &&
    [slotDimensions.width, slotDimensions.height].every(
      (dimension) =>
        Number.isInteger(dimension) &&
        dimension >= 1 &&
        dimension <= AD_DIMENSION_MAX_PX,
    );
  const reservedWidth = validSlotDimensions ? slotDimensions.width : 468;
  const reservedHeight = validSlotDimensions ? slotDimensions.height : 60;
  const frameWidth = reserveEmptySpace
    ? reservedWidth
    : (displayedAd?.image_width ?? reservedWidth);
  const frameHeight = reserveEmptySpace
    ? reservedHeight
    : (displayedAd?.image_height ?? reservedHeight);
  const frameClassName = "block w-full";
  const frameStyle: CSSProperties = {
    aspectRatio: `${frameWidth} / ${frameHeight}`,
    maxWidth: `min(${frameWidth}px, ${(50 * frameWidth) / frameHeight}svh)`,
    maxHeight: "50svh",
  };

  const presentationKey = [
    category,
    slot,
    displayedAd?.id ?? "",
    imageUrl ?? "",
    concealR18 ? "concealed" : "visible",
  ].join("\u0000");

  return (
    <aside
      aria-label={imageUrl ? "広告" : undefined}
      aria-hidden={displayedAd !== undefined && !imageUrl ? true : undefined}
      aria-busy={displayedAd === undefined ? true : undefined}
      className="flex justify-center border-b border-border bg-background px-2 py-2"
    >
      <AdFrame
        key={presentationKey}
        ad={displayedAd}
        clickUrl={clickUrl}
        frameClassName={frameClassName}
        frameStyle={frameStyle}
        imageUrl={imageUrl}
        priority={priority}
        concealR18={concealR18}
      />
    </aside>
  );
};

interface AdFrameProps {
  ad: Ad | null | undefined;
  clickUrl: string | null;
  frameClassName: string;
  frameStyle: CSSProperties;
  imageUrl: string | null;
  priority: boolean;
  concealR18: boolean;
}

const AdFrame: FunctionComponent<AdFrameProps> = ({
  ad,
  clickUrl,
  frameClassName,
  frameStyle,
  imageUrl,
  priority,
  concealR18,
}: AdFrameProps) => {
  const [revealed, setRevealed] = useState(false);
  const linkRef = useRef<HTMLAnchorElement>(null);
  const figureRef = useRef<HTMLElement>(null);
  const transferFocus = useRef(false);
  useLayoutEffect(() => {
    if (revealed && transferFocus.current) {
      (linkRef.current ?? figureRef.current)?.focus({ preventScroll: true });
      transferFocus.current = false;
    }
  }, [revealed]);
  const isConcealed =
    concealR18 && ad?.category === "r18" && imageUrl !== null && !revealed;
  const imageAlt = ad?.image_alt?.trim() || ad?.title.trim() || "広告";

  const image = imageUrl && (
    <img
      src={imageUrl}
      alt={isConcealed ? "" : imageAlt}
      aria-hidden={isConcealed ? true : undefined}
      loading={priority ? "eager" : "lazy"}
      fetchPriority={priority ? "high" : undefined}
      // 配信元(storage.nijiurachan.net)はリファラ(ホットリンク)保護があり、
      // 許可ドメイン以外（localhost 等）からの Referer を 403 で弾く。
      // Referer を送らなければ環境を問わず取得でき、閲覧中スレッドのURLを
      // 広告CDNへ漏らさないプライバシー面のメリットもある。
      referrerPolicy="no-referrer"
      className={`h-full w-full object-contain${isConcealed ? " r18-spoiler-image" : ""}`}
    />
  );

  if (!imageUrl) {
    return <div className={frameClassName} style={frameStyle} />;
  }

  if (isConcealed) {
    return (
      <button
        type="button"
        aria-label="R18広告を表示"
        onClick={(event: MouseEvent<HTMLButtonElement>): void => {
          transferFocus.current =
            document.activeElement === event.currentTarget;
          setRevealed(true);
        }}
        className={`${frameClassName} relative cursor-pointer border-0 bg-transparent p-0 text-inherit focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary`}
        style={frameStyle}
      >
        {image}
        <span
          aria-hidden="true"
          className="absolute inset-0 z-10 flex items-center justify-center bg-background/80 px-2 text-center font-medium text-foreground"
        >
          R18広告を表示
        </span>
      </button>
    );
  }

  return clickUrl ? (
    <a
      ref={linkRef}
      href={clickUrl}
      target="_blank"
      rel="noopener noreferrer sponsored"
      className={frameClassName}
      style={frameStyle}
    >
      {image}
    </a>
  ) : (
    <figure
      ref={figureRef}
      tabIndex={revealed ? -1 : undefined}
      className={frameClassName}
      style={frameStyle}
    >
      {image}
    </figure>
  );
};
